import { CaptionsRenderer } from '@sparemin/text-effects';
import React, { useState } from 'react';
import { isEqual, throttle } from 'underscore';

import {
  AnimationConfigGetterParams,
  getAdvancedAnimationPreviewConfig,
} from '../extensions/AnimationsTab';
import { useTextOverlay } from '../state/TextOverlayProvider';
import { scaleEditorDataSize } from '../style-transform-utils';
import useTextOverlayCaptionsResize from './useTextOverlayCaptionsResize';
import { TextOverlayResizeObserver } from './useTextOverlayResizeDelta';

const STATE_UPDATER_THROTLE_MS = 5;

interface UseTextOverlayCaptionsAnimations {
  resizeDeltaObserver: TextOverlayResizeObserver;
}

const useTextOverlayCaptionsAnimations = (): UseTextOverlayCaptionsAnimations => {
  const {
    canvasSize,
    draftEditorData,
    editorHtmlRef,
    textValue,
  } = useTextOverlay();

  const isResizingRef = React.useRef<boolean>(false);
  const captionsRendererRef = React.useRef<CaptionsRenderer | undefined>();

  const advancedAnimation = React.useMemo(
    () => draftEditorData.get('advancedAnimation')?.toJS(),
    [draftEditorData],
  );

  const textStyles = React.useMemo(
    () => draftEditorData.getIn(['editor', 'textStyle'])?.toJS(),
    [draftEditorData],
  );

  const boxSize = React.useMemo(() => {
    if (!draftEditorData) {
      return undefined;
    }

    return scaleEditorDataSize(
      {
        height: draftEditorData.getIn(['size', 'height']),
        width: draftEditorData.getIn(['size', 'width']),
      },
      draftEditorData,
      canvasSize,
    );
  }, [canvasSize, draftEditorData]);

  const pickedObservableConfig = React.useMemo(
    () => ({
      advancedAnimation,
      boxSize,
      canvasSize,
      textStyles,
      textValue,
    }),
    [advancedAnimation, boxSize, canvasSize, textStyles, textValue],
  );
  const [prevPickedObservableConfig, setPrevPickedObservableConfig] = useState<
    AnimationConfigGetterParams
  >(pickedObservableConfig);

  /**
   * Mount control, mounts the effect renderer when it has not been set yet.
   * It will skip if the animation renderer is already set.
   */
  React.useEffect(() => {
    const captionsContentElement = editorHtmlRef?.current?.querySelector(
      'span',
    );

    if (
      !captionsContentElement ||
      !pickedObservableConfig.advancedAnimation?.enabled ||
      captionsRendererRef?.current
    ) {
      return;
    }

    const config = getAdvancedAnimationPreviewConfig({
      advancedAnimation: pickedObservableConfig.advancedAnimation,
      boxSize: pickedObservableConfig.boxSize,
      canvasSize: pickedObservableConfig.canvasSize,
      textStyles: pickedObservableConfig.textStyles,
      textValue: pickedObservableConfig.textValue,
    });

    captionsRendererRef.current = new CaptionsRenderer(
      captionsContentElement,
      config,
    );

    requestAnimationFrame(() => {
      captionsRendererRef?.current?.getAnimator();
    });
  }, [editorHtmlRef, pickedObservableConfig]);

  /**
   * It is necessary to throtle the config updater for improving perfomance and also
   * some race conditions that can produce a blinking due to the amount of consecutive
   * state updates.
   */
  const pickedConfigUpdater = React.useMemo(
    () =>
      throttle((config: AnimationConfigGetterParams) => {
        setPrevPickedObservableConfig(config);

        requestAnimationFrame(() => {
          captionsRendererRef.current?.updateConfig(
            getAdvancedAnimationPreviewConfig(
              {
                advancedAnimation: config.advancedAnimation,
                boxSize: config.boxSize,
                canvasSize: config.canvasSize,
                textStyles: config.textStyles,
                textValue: config.textValue,
              },
              isResizingRef.current,
            ),
          );
        });
      }, STATE_UPDATER_THROTLE_MS),
    [],
  );

  if (!isEqual(prevPickedObservableConfig, pickedObservableConfig)) {
    pickedConfigUpdater(pickedObservableConfig);
  }

  const resizeDeltaObserver = useTextOverlayCaptionsResize({
    rendererRef: captionsRendererRef,
    isResizingRef,
    pickedObservableConfig,
  });

  return {
    resizeDeltaObserver,
  };
};

export default useTextOverlayCaptionsAnimations;
